/* eslint-disable max-classes-per-file */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

abstract class SearchByNameEvent extends AnalyticsEvent {
  abstract eventName: string;

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.recommendations.browse_search;
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person opens the search-by-name modal (i.e. selects the
 * name-search "tab"). The `tab` property is fixed to `'name'` on web because
 * the regular search "tab" is the default page; the property exists so that
 * analytics consumers can pivot the two flows together.
 *
 * &nbsp;
 */
export class SearchNameTabSelectedEvent extends SearchByNameEvent {
  eventName = 'search-name-tab-selected';

  constructor(eventProperties: { tab: 'name' | 'service' } = { tab: 'name' }) {
    super(eventProperties, { tab: eventProperties.tab });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person submits a search-by-name query. `searchQuery` is
 * intentionally omitted pending Privacy/Legal review; the metadata-only
 * properties below describe the query shape without exposing the raw text.
 *
 * &nbsp;
 */
export class SearchNameQuerySubmittedEvent extends SearchByNameEvent {
  eventName = 'search-name-query-submitted';

  constructor(eventProperties: {
    wordCount: number;
    length: number;
    queryNumberInSession: number;
  }) {
    super(eventProperties, {
      wordCount: eventProperties.wordCount,
      length: eventProperties.length,
      queryNumberInSession: eventProperties.queryNumberInSession,
    });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when search-by-name results are returned and committed to state. The
 * `searchUuid` ties the event back to the originating `query-submitted` event.
 *
 * &nbsp;
 */
export class SearchNameResultsRenderedEvent extends SearchByNameEvent {
  eventName = 'search-name-results-rendered';

  constructor(eventProperties: {
    resultCount: number;
    isZeroResults: boolean;
    searchUuid: string | null;
    latencyMs: number;
  }) {
    super(eventProperties, {
      resultCount: eventProperties.resultCount,
      isZeroResults: eventProperties.isZeroResults,
      searchUuid: eventProperties.searchUuid,
      latencyMs: eventProperties.latencyMs,
    });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person clicks a sitter card inside the search-by-name results
 * list. `matchTier` is intentionally not emitted in v1 — score-threshold
 * inference would silently mis-bin the experiment readout.
 *
 * &nbsp;
 */
export class SearchNameCardClickedEvent extends SearchByNameEvent {
  eventName = 'search-name-card-clicked';

  constructor(eventProperties: {
    rank: number;
    sitterPersonOpk: string;
    resultCount: number;
    searchUuid: string;
    isPreviouslyBooked: boolean;
  }) {
    super(eventProperties, {
      rank: eventProperties.rank,
      sitterPersonOpk: eventProperties.sitterPersonOpk,
      resultCount: eventProperties.resultCount,
      searchUuid: eventProperties.searchUuid,
      isPreviouslyBooked: eventProperties.isPreviouslyBooked,
    });
  }
}
